import type { RefineThemedLayoutV2HeaderProps } from "@refinedev/antd";
import { useGetIdentity } from "@refinedev/core";
import {
  Layout as AntdLayout,
  Avatar,
  Space,
  theme,
  Typography,
  Dropdown,
  Button,
  MenuProps,
  Modal,
  Alert,
} from "antd";
import React, { useState } from "react";
import { 
  UserOutlined, 
  LogoutOutlined, 
  QuestionCircleOutlined,
  CodeOutlined,
  BulbOutlined,
  TeamOutlined,
  SettingOutlined,
  FileSearchOutlined,
  UploadOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";

const { Text, Title, Paragraph } = Typography;
const { useToken } = theme;

type IUser = {
  id: number;
  name: string;
  avatar: string;
};

export const Header: React.FC<RefineThemedLayoutV2HeaderProps> = ({
  sticky = true,
}) => {
  const { token } = useToken();
  const { data: user } = useGetIdentity<IUser>();
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const [showOrgGuide, setShowOrgGuide] = useState(false);
  const [showCodeGuide, setShowCodeGuide] = useState(false);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleProfileClick = () => {
    navigate("/profile");
  };

  const helpMenuItems: MenuProps['items'] = [
    {
      key: 'org-guide',
      icon: <TeamOutlined />,
      label: 'Organization Management Guide',
      onClick: () => setShowOrgGuide(true),
    },
    {
      key: 'code-guide',
      icon: <CodeOutlined />,
      label: 'Code Identifier Guide',
      onClick: () => setShowCodeGuide(true),
    },
  ];

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'View Profile',
      onClick: handleProfileClick,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
      danger: true,
    },
  ];

  const headerStyles: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "0px 24px",
    height: "64px",
  };

  if (sticky) {
    headerStyles.position = "sticky";
    headerStyles.top = 0;
    headerStyles.zIndex = 1;
  }
  
  return (
    <>
      <AntdLayout.Header style={headerStyles}>
        <Space style={{ marginLeft: "8px" }} size="middle">
          <Dropdown 
            menu={{ items: helpMenuItems }} 
            placement="bottomRight"
            trigger={['click']}
          >
            <Button 
              type="text" 
              icon={<QuestionCircleOutlined />}
              style={{ 
                color: token.colorText,
                fontSize: '16px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              Help
            </Button>
          </Dropdown>
          {user?.name && <Text strong>{user.name}</Text>}
          <Dropdown 
            menu={{ items: profileMenuItems }} 
            placement="bottomRight"
            trigger={['click']}
          >
            <Avatar 
              src={user?.avatar} 
              icon={<UserOutlined />}
              style={{ cursor: 'pointer' }}
            />
          </Dropdown>
        </Space>
      </AntdLayout.Header>

      {/* Organization Management Guide Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TeamOutlined style={{ color: '#1890ff' }} />
            <span>Organization Management Guide</span>
          </div>
        }
        open={showOrgGuide}
        onCancel={() => setShowOrgGuide(false)}
        footer={null}
        width={700}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
            The Organization menu contains all the tools you need to manage your team, billing, and settings. 
            Here's what each section does:
          </Paragraph>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <SettingOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
              <Title level={5} style={{ margin: 0, color: '#1890ff' }}>Organization</Title>
            </div>
            <Paragraph style={{ marginLeft: '26px', color: '#666', marginBottom: 0 }}>
              View and edit your organization's basic information, settings, and general configuration.
            </Paragraph>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <TeamOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              <Title level={5} style={{ margin: 0, color: '#52c41a' }}>Facilities</Title>
            </div>
            <Paragraph style={{ marginLeft: '26px', color: '#666', marginBottom: 0 }}>
              Create and manage different locations or departments. Add team members to facilities, 
              generate invite tokens, and track usage for each location.
            </Paragraph>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <SafetyCertificateOutlined style={{ color: '#faad14', fontSize: '18px' }} />
              <Title level={5} style={{ margin: 0, color: '#faad14' }}>Subscription</Title>
            </div>
            <Paragraph style={{ marginLeft: '26px', color: '#666', marginBottom: 0 }}>
              Manage your billing and subscription. Set up payment methods, choose billing cycles, 
              monitor trial status, and view facility usage and costs.
            </Paragraph>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FileSearchOutlined style={{ color: '#722ed1', fontSize: '18px' }} />
              <Title level={5} style={{ margin: 0, color: '#722ed1' }}>Invoices</Title>
            </div>
            <Paragraph style={{ marginLeft: '26px', color: '#666', marginBottom: 0 }}>
              Access your billing history, download invoices, track payment status, 
              and view detailed billing information for your organization.
            </Paragraph>
          </div>

          <Alert
            message="Need More Help?"
            description="Contact our support team at help@flycodepilot.com for personalized assistance with organization setup."
            type="success"
            showIcon
            style={{ marginTop: '24px' }}
          />
        </div>
      </Modal>

      {/* Code Identifier Guide Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CodeOutlined style={{ color: '#52c41a' }} />
            <span>Code Identifier Guide</span>
          </div>
        }
        open={showCodeGuide}
        onCancel={() => setShowCodeGuide(false)}
        footer={null}
        width={700}
        styles={{ body: { padding: '24px' } }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Alert
            message="AI-Powered Medical Code Identification"
            description="Upload medical documents and get accurate ICD-10 codes with detailed explanations."
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <UploadOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                <Title level={5} style={{ margin: 0, color: '#1890ff' }}>Upload Medical Files</Title>
              </div>
              <Paragraph style={{ marginLeft: '26px', color: '#666', marginBottom: 0 }}>
                Select and upload PDF documents containing medical information.
              </Paragraph>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <BulbOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                <Title level={5} style={{ margin: 0, color: '#52c41a' }}>AI Processing</Title>
              </div>
              <Paragraph style={{ marginLeft: '26px', color: '#666', marginBottom: 0 }}>
                Our AI analyzes the documents and extracts medical conditions.
              </Paragraph>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <FileSearchOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                <Title level={5} style={{ margin: 0, color: '#faad14' }}>Review Results</Title>
              </div>
              <Paragraph style={{ marginLeft: '26px', color: '#666', marginBottom: 0 }}>
                Get organized ICD-10 codes with explanations and source references.
              </Paragraph>
            </div>
          </div>

          <Title level={4} style={{ marginTop: '24px', marginBottom: '16px', color: '#1f1f1f' }}>
            How to Use
          </Title>
          
          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UploadOutlined style={{ color: '#1890ff' }} />
              File Upload
            </Title>
            <Paragraph style={{ marginLeft: '28px', color: '#666' }}>
              • Drag and drop PDF files or click to browse<br/>
              • Multiple files can be uploaded simultaneously<br/>
              • Supports standard medical document formats
            </Paragraph>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CodeOutlined style={{ color: '#52c41a' }} />
              Code Results
            </Title>
            <Paragraph style={{ marginLeft: '28px', color: '#666' }}>
              • Click arrows to expand each condition for details<br/>
              • Copy individual codes or all codes at once<br/>
              • View AI reasoning and source text snippets<br/>
              • Codes are organized by medical category
            </Paragraph>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <Title level={5} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SafetyCertificateOutlined style={{ color: '#faad14' }} />
              Best Practices
            </Title>
            <Paragraph style={{ marginLeft: '28px', color: '#666' }}>
              • Upload clear, complete medical documents<br/>
              • Review all suggested codes with qualified medical staff<br/>
              • Use provider notes context to validate AI suggestions<br/>
              • Always verify codes before clinical or billing use
            </Paragraph>
          </div>

          <Alert
            message="⚠️ Medical Disclaimer"
            description="CodePilot assists with ICD-10 identification but may make mistakes. Always verify codes with qualified medical staff before clinical or billing decisions."
            type="warning"
            showIcon
            style={{ marginTop: '24px' }}
          />
        </div>
      </Modal>
    </>
  );
};
